import { serializeJsonLd } from "~/lib/structuredData";
import clsx from "clsx";

import FAQCollapse from "~/common/components/FAQCollapse";
import SectionWrapper from "~/common/components/SectionWrapper";

import styles from "./SectionFAQ.module.scss";

/**
 * Generic FAQ Section Component
 * Props:
 * - title: string - translated section title
 * - description: string - translated section description
 * - faqData: Array<{question: string, answer: string}> | Array<{group: true, title: string, questions: Array<{question: string, answer: string}>}> - FAQ data array
 * - className?: string - additional CSS class
 * - sectionClassName?: string - CSS class for SectionWrapper
 */
export default function SectionFAQ({ title, description, faqData, className, sectionClassName, pageUrl, locale }) {
  const faqItems =
    faqData
      ?.flatMap((faq) => (faq.group ? faq.questions || [] : [faq]))
      .filter((item) => typeof item?.question === "string" && typeof item?.answer === "string") || [];

  const faqSchema =
    faqItems.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          ...(pageUrl ? { "@id": `${pageUrl}#faq`, isPartOf: { "@id": `${pageUrl}#webpage` } } : {}),
          ...(locale ? { inLanguage: locale } : {}),
          mainEntity: faqItems.map((item) => ({
            "@type": "Question",
            name: item.question,
            acceptedAnswer: {
              "@type": "Answer",
              text: item.answer,
            },
          })),
        }
      : null;

  return (
    <SectionWrapper className={clsx(styles.section, sectionClassName)}>
      {faqSchema && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: serializeJsonLd(faqSchema) }} />
      )}
      <h2>{title}</h2>
      <p>{description}</p>
      <ol className={clsx(styles.faq, "card", className)} style={{ listStyle: "none", padding: 0, margin: 0 }}>
        {faqData?.map((faq, idx) => {
          const faqKey = faq.id || faq.question || faq.title || idx;
          if (faq.group) {
            return (
              <li key={faqKey} className={styles.faqGroup}>
                <h3 className={styles.groupTitle}>{faq.title}</h3>
                <ol className={styles.groupQuestions} style={{ listStyle: "none", padding: 0, margin: 0 }}>
                  {faq.questions?.map((question, qIdx) => {
                    const questionKey = question.id || question.question || qIdx;
                    return (
                      <li key={questionKey}>
                        <FAQCollapse title={question.question}>
                          <div className={styles.faqContent}>{question.answer}</div>
                        </FAQCollapse>
                      </li>
                    );
                  })}
                </ol>
              </li>
            );
          }

          return (
            <li key={faqKey}>
              <FAQCollapse title={faq.question}>
                <div className={styles.faqContent}>{faq.answer}</div>
              </FAQCollapse>
            </li>
          );
        })}
      </ol>
    </SectionWrapper>
  );
}
